import { NavLink } from 'react-router-dom';
import vistaLogo from '../../assets/vista-logo.svg';
import { useAuth } from '../../context/AuthContext.jsx';

// v0.13.3: Alerts removed from the sidebar — it now lives as a bell
// icon in the header next to the settings gear (Header.jsx). The bell
// carries the same active-count badge.
// Ordered by operator-facing importance, descending. Subscribers and
// Flows were removed from the nav (their routes still resolve by URL).
// Services sits third when the per-org network_services_enabled flag is
// on; it's filtered out below when the flag is off, collapsing the list
// to Overview / Topology / Devices.
const NAV_ITEMS = [
  { to: '/', label: 'Overview', icon: '⊞' },
  { to: '/topology', label: 'Topology', icon: '◎' },
  // S15-7 (#557): WAN/uplink connectivity — globe glyph for the path to the
  // internet (#791 review: the up-arrow didn't read as "internet/WAN").
  { to: '/uplinks', label: 'WAN / Uplink', icon: '🌐' },
  // #744: agent + proxy host/hardware health.
  { to: '/hosts', label: 'Host Health', icon: '🖥' },
  // S14-18: gated below on the per-org network_services_enabled flag.
  { to: '/services', label: 'Services', icon: '◈', flag: 'networkServicesEnabled' },
  { to: '/devices', label: 'Devices', icon: '▦' },
  // Fleet-wide syslog browser. Sits after Devices because it is the page you
  // reach FROM a device question ("what did it say?") rather than one you
  // start from. Before it existed the raw syslog stream was write-only — the
  // agent analysed each line, stored the signature hits, and dropped the line.
  { to: '/logs', label: 'Logs', icon: '☰' },
  // vista#956: sits directly under Logs on purpose. The question it answers —
  // "this device is missing from my logs; is it being refused?" — is asked
  // from Logs, and a default-deny receiver makes "not sending" and "sending
  // and dropped before parsing" look identical. org_ro reach, because the
  // person who notices the gap is usually a viewer; the approve/deny controls
  // are gated inside the page.
  { to: '/event-sources', label: 'Event Sources', icon: '⇥' },
  // #789: Subscribers + Flows were built + routed but had no nav entry —
  // reachable only by typing the URL. Both are org-level (org_ro+) reads.
  { to: '/subscribers', label: 'Subscribers', icon: '👥' },
  { to: '/flows', label: 'Flows', icon: '⇄' },
];

export default function Sidebar() {
  const { networkServicesEnabled } = useAuth();
  const flags = { networkServicesEnabled };
  const items = NAV_ITEMS.filter((item) => !item.flag || flags[item.flag]);
  return (
    <aside className="flex w-56 flex-col border-r border-gray-200 bg-white">
      <div className="flex h-14 items-center border-b border-gray-200 px-3">
        <img src={vistaLogo} alt="VISTA" className="h-12" />
      </div>
      <nav className="flex-1 space-y-0.5 p-2">
        {items.map(({ to, label, icon }) => (
          <NavLink
            key={to}
            to={to}
            end={to === '/'}
            className={({ isActive }) =>
              `flex items-center gap-2.5 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                isActive
                  ? 'bg-blue-50 text-blue-700'
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
              }`
            }
          >
            <span className="w-5 text-center">{icon}</span>
            <span className="flex-1">{label}</span>
          </NavLink>
        ))}
      </nav>
    </aside>
  );
}
